import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Search, MapPin } from "lucide-react";
import { getPrewarmedCities } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";

export default function InstantCities() {
  const [data, setData] = useState({ cities: [], total_articles: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    getPrewarmedCities(300)
      .then((d) => { if (alive) setData(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.cities;
    return data.cities.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.state || "").toLowerCase().includes(q)
    );
  }, [data.cities, query]);

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12" data-testid="instant-cities-page">
      <Seo
        title="Instant Cities — Ready-to-Read City Guides"
        path="/cities"
        description="Hundreds of U.S. cities with pre-generated, in-depth guides on USAPulse — history, geography, economy, culture and more, ready to read instantly."
      />
      <SectionHeading
        kicker="Pre-warmed for instant reading"
        title="Instant Cities"
      />

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm text-muted-foreground">
          <T>
            These cities have their key articles — history, geography, economy, culture and more —
            pre-generated and cached, so their pages open instantly. New places are added every night.
          </T>
        </p>
        <div className="flex shrink-0 items-center gap-4">
          <div className="rounded-xl bg-navy px-4 py-2 text-center text-white" data-testid="instant-cities-stat">
            <div className="font-display text-2xl font-bold leading-none">{data.count}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/70">Cities ready</div>
          </div>
          <div className="rounded-xl bg-oldglory-red px-4 py-2 text-center text-white" data-testid="instant-articles-stat">
            <div className="font-display text-2xl font-bold leading-none">{data.total_articles}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/80">Articles cached</div>
          </div>
        </div>
      </div>

      <div className="relative mb-8 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter cities or states…"
          className="w-full rounded-full border border-border bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/20"
          data-testid="instant-cities-search"
        />
      </div>

      {loading ? (
        <div className="py-24 text-center text-muted-foreground" data-testid="instant-cities-loading">
          <T>Loading instant cities…</T>
        </div>
      ) : list.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground" data-testid="instant-cities-empty">
          <T>No cities match your filter yet.</T>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {list.map((c, i) => (
            <motion.div
              key={c.geo_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i, 20) * 0.03, duration: 0.35 }}
            >
              <Link
                to={`/place${c.url_path}`}
                className="group flex h-full flex-col justify-between rounded-xl border border-border bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-xl"
                data-testid={`instant-city-card-${c.geo_id}`}
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-base font-bold leading-tight text-navy">{c.name}</div>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-bold text-navy">
                      <Zap className="h-3 w-3 fill-oldglory-red text-oldglory-red" />
                      {c.article_count}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {c.state}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
                  <span>{c.population ? c.population.toLocaleString() : "—"}</span>
                  <span className="font-semibold text-oldglory-red opacity-0 transition-opacity group-hover:opacity-100">
                    <T>Open instantly →</T>
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
